import { createSlice } from "@reduxjs/toolkit";
import { createBrowserRouter } from "react-router-dom";
import { LOCAL_ROUTES } from "../RouterComponent/RouterComponent";
import ProtectedRoute from "../RouterComponent/ProtectedRoute";
import ODataReportViewer from "../Components/ReportViewers/ODataReportViewer";
import HTTPReportViewer from "../Components/ReportViewers/HTTPReportViewer";
import { REPORTS } from "../../utils/services/apiPath";
import { getApi } from "../../utils/services";
import { createODataQuery } from "../../utils/services/oData";
import ODataReportDesigner from "../Components/ReportViewers/ODataReportDesigner";
import HTTPReportDesigner from "../Components/ReportViewers/HTTPReportDesigner";

const initialState = {
  routes: LOCAL_ROUTES,
  router: null,
  defaultReportRoutes: [],
  customReportRoutes: [],
  apiRoutesAdded: false,
  designerComponents: []
};

const hasAsyncDownloadMetadata = report =>
  Object.prototype.hasOwnProperty.call(report, "enableAsyncDownload") &&
  Object.prototype.hasOwnProperty.call(report, "reportApiRoute");

const hasMultiProjectPartnerFilterMetadata = report =>
  Object.prototype.hasOwnProperty.call(report?.filters ?? {}, "multiProjectPartnerFilter");

const normalizeAsyncDownloadMetadata = report => {
  const route = typeof report?.reportApiRoute === "string" ? report.reportApiRoute.trim() : "";
  return {
    enableAsyncDownload: report?.enableAsyncDownload === true,
    reportApiRoute: route.length > 0 ? route : null
  };
};

const handleCustomReportInitialize = async dispatch => {
  try {
    const apiRoutes = [];
    const routes = localStorage.getItem("customReports");
    if (!routes) {
      const localStorageRoutes = [];
      const { data } = await getApi(
        REPORTS + createODataQuery(["isDefault"], [], "isDefault eq false", null, { orderCol: "reportCategory", orderByAscending: true }, 0, 0, false)
      );
      data.forEach(report => {
        const path = report?.reportName.toLowerCase().trim().split(" ").join("-");
        const filePath = `/${report?.reportFile?.path}/${report?.reportFile?.fileName}`;
        const routeData = {
          path: `${path}-custom`,
          element: <ProtectedRoute Component={report.dataType === "odata" ? ODataReportViewer : HTTPReportViewer} props={{ fileName: filePath }} />
        };
        apiRoutes.push(routeData);
        localStorageRoutes.push({
          ...routeData,
          designerPath: `designer-${path}-custom`,
          reportId: report.reportId,
          reportCategoryId: report.reportCategoryId,
          type: report.dataType,
          ...normalizeAsyncDownloadMetadata(report),
          filePath: filePath,
          category: report.reportCategory,
          title: report?.reportName,
          isDefault: report.isDefault,
          filters: {
            apprenticeFilter: report?.apprenticeFilter,
            dateFilter: report?.dateFilter,
            mentorFilter: report?.mentorFilter,
            partnerFilter: report?.partnerFilter,
            projectFilter: report?.projectFilter,
            multiProjectPartnerFilter: report?.multiProjectPartnerFilter === true
          }
        });
      });
      localStorage.setItem("customReports", JSON.stringify(localStorageRoutes));
      dispatch(setCustomReportRoutes(localStorageRoutes));
      dispatch(setRoutes(LOCAL_ROUTES.concat(apiRoutes)));
    } else {
      const parsed = JSON.parse(routes);
      const needsMetadataRefresh = parsed.some(report => !hasAsyncDownloadMetadata(report) || !hasMultiProjectPartnerFilterMetadata(report));
      if (needsMetadataRefresh) {
        localStorage.removeItem("customReports");
        return await handleCustomReportInitialize(dispatch);
      }
      const normalizedParsed = parsed.map(report => ({
        ...report,
        ...normalizeAsyncDownloadMetadata(report)
      }));
      const apiRoutes = [];
      normalizedParsed.forEach(report => {
        const routeData = {
          path: report.path,
          element: <ProtectedRoute Component={report.type === "odata" ? ODataReportViewer : HTTPReportViewer} props={{ fileName: report.filePath }} />
        };
        apiRoutes.push(routeData);
      });
      localStorage.setItem("customReports", JSON.stringify(normalizedParsed));
      dispatch(setCustomReportRoutes(normalizedParsed));
      dispatch(setRoutes(LOCAL_ROUTES.concat(apiRoutes)));
    }
  } catch (e) {
    console.log(e);
  }
};

const handleStandardReportsInitialize = async (dispatch, getState) => {
  try {
    const apiRoutes = [];
    const inStorage = localStorage.getItem("standardReports");
    const { router: routerState } = getState();
    const { routes } = routerState;
    if (!inStorage) {
      const localStorageRoutes = [];
      const { data } = await getApi(
        REPORTS + createODataQuery(["isDefault"], [], "isDefault eq true", null, { orderCol: "reportCategory", orderByAscending: true }, 0, 0, false)
      );
      data.forEach(report => {
        const path = report?.reportName.toLowerCase().trim().split(" ").join("-");
        const filePath = `${report?.reportFile?.path}/${report?.reportFile?.fileName}`;
        const routeData = {
          path: path,
          element: <ProtectedRoute Component={report.dataType === "odata" ? ODataReportViewer : HTTPReportViewer} props={{ fileName: filePath }} />
        };
        apiRoutes.push(routeData);
        localStorageRoutes.push({
          ...routeData,
          designerPath: `designer-${path}`,
          reportId: report.reportId,
          type: report.dataType,
          ...normalizeAsyncDownloadMetadata(report),
          reportCategoryId: report.reportCategoryId,
          filePath: filePath,
          category: report.reportCategory,
          title: report?.reportName,
          isDefault: report.isDefault,
          filters: {
            apprenticeFilter: report?.apprenticeFilter,
            dateFilter: report?.dateFilter,
            mentorFilter: report?.mentorFilter,
            partnerFilter: report?.partnerFilter,
            projectFilter: report?.projectFilter,
            multiProjectPartnerFilter: report?.multiProjectPartnerFilter === true
          }
        });
      });
      localStorage.setItem("standardReports", JSON.stringify(localStorageRoutes));
      dispatch(setDefaultReportRoutes(localStorageRoutes));
      dispatch(setRoutes(routes.concat(apiRoutes)));
    } else {
      const parsed = JSON.parse(inStorage);
      const needsMetadataRefresh = parsed.some(report => !hasAsyncDownloadMetadata(report) || !hasMultiProjectPartnerFilterMetadata(report));
      if (needsMetadataRefresh) {
        localStorage.removeItem("standardReports");
        return await handleStandardReportsInitialize(dispatch, getState);
      }
      const normalizedParsed = parsed.map(report => ({
        ...report,
        ...normalizeAsyncDownloadMetadata(report)
      }));
      const apiRoutes = [];
      normalizedParsed.forEach(report => {
        const routeData = {
          path: report.path,
          element: <ProtectedRoute Component={report.type === "odata" ? ODataReportViewer : HTTPReportViewer} props={{ fileName: report.filePath }} />
        };
        apiRoutes.push(routeData);
      });
      localStorage.setItem("standardReports", JSON.stringify(normalizedParsed));
      dispatch(setDefaultReportRoutes(normalizedParsed));
      dispatch(setRoutes(routes.concat(apiRoutes)));
    }
  } catch (e) {
    console.log(e);
  }
};

const initializeDesignerRoutes = (dispatch, getState) => {
  const { router: routerState } = getState();
  const { customReportRoutes, defaultReportRoutes } = routerState;
  const allRoutes = customReportRoutes.concat(defaultReportRoutes);

  const designerRoutes = allRoutes.map(route => {
    const isOData = route.type == "odata";
    const component = isOData ? ODataReportDesigner : HTTPReportDesigner;
    return {
      path: `/designer-${route.path}`,
      element: <ProtectedRoute Component={component} props={{ fileName: route.filePath }} />
    };
  });
  dispatch(setDesignerRoutes(designerRoutes));
};

export const handleApiRouterInitialize = () => {
  return async (dispatch, getState) => {
    await handleCustomReportInitialize(dispatch);
    await handleStandardReportsInitialize(dispatch, getState);
    initializeDesignerRoutes(dispatch, getState);

    const { router: routerState } = getState();
    const { routes, designerComponents } = routerState;
    dispatch(setRouter(createBrowserRouter(routes.concat(designerComponents))));
  };
};

export const reloadCustomReports = () => {
  return async dispatch => {
    const apiRoutes = [];
    const localStorageRoutes = [];
    const { data } = await getApi(
      REPORTS + createODataQuery(["isDefault"], [], "isDefault eq false", null, { orderCol: "reportCategory", orderByAscending: true }, 0, 0, false)
    );
    data.forEach(report => {
      const path = report?.reportName.toLowerCase().trim().split(" ").join("-");
      const filePath = `/${report?.reportFile?.path}/${report?.reportFile?.fileName}`;
      const routeData = {
        path: `${path}-custom`,
        element: <ProtectedRoute Component={report.dataType === "odata" ? ODataReportViewer : HTTPReportViewer} props={{ fileName: filePath }} />
      };
      apiRoutes.push(routeData);
      localStorageRoutes.push({
        ...routeData,
        designerPath: `designer-${path}-custom`,
        reportId: report.reportId,
        reportCategoryId: report.reportCategoryId,
        type: report.dataType,
        ...normalizeAsyncDownloadMetadata(report),
        filePath: filePath,
        category: report.reportCategory,
        title: report?.reportName,
        isDefault: report.isDefault,
        filters: {
          apprenticeFilter: report?.apprenticeFilter,
          dateFilter: report?.dateFilter,
          mentorFilter: report?.mentorFilter,
          partnerFilter: report?.partnerFilter,
          projectFilter: report?.projectFilter,
          multiProjectPartnerFilter: report?.multiProjectPartnerFilter === true
        }
      });
    });
    localStorage.setItem("customReports", JSON.stringify(localStorageRoutes));
    dispatch(setCustomReportRoutes(localStorageRoutes));
    dispatch(setRoutes(LOCAL_ROUTES.concat(apiRoutes)));
  };
};

const routerSlice = createSlice({
  name: "router",
  initialState,
  reducers: {
    setRoutes: (state, action) => {
      state.routes = action.payload;
    },
    setDefaultReportRoutes: (state, action) => {
      state.defaultReportRoutes = action.payload;
    },
    setCustomReportRoutes: (state, action) => {
      state.customReportRoutes = action.payload;
    },
    setDesignerRoutes: (state, action) => {
      state.designerComponents = action.payload;
    },
    setRouter: (state, action) => {
      state.router = action.payload;
      state.apiRoutesAdded = true;
    }
  }
});

export const { setRoutes, setRouter, setDefaultReportRoutes, setCustomReportRoutes, setDesignerRoutes } = routerSlice.actions;
export const routerSelector = state => state.router;
export default routerSlice.reducer;
